// Command palette (Ctrl/Cmd + K). Vanilla JS, fuzzy match.

import { theme } from "./prefs.js";
import { themePicker } from "./theme_picker.js";
import { t, accentName } from "../utils/i18n.js";
import { fuzzyFilter } from "../utils/fuzzy.js";

const ROOT = "e16-cmdk-root";

function close() {
	const el = document.getElementById(ROOT);
	if (el) el.remove();
}

function buildCommands() {
	const lng = theme.currentLang();
	const cmds = [];

	for (const a of theme.VALID_ACCENTS) {
		cmds.push({
			id: `accent:${a}`,
			title: `${t("Switch theme to", lng)} ${accentName(a, lng)}`,
			hint: a,
			run: () => theme.applyAccent(a),
		});
	}

	cmds.push({
		id: "mode:toggle",
		title: t("Toggle dark mode", lng),
		hint: theme.currentMode() === "light" ? "→ Dark" : "→ Light",
		run: () => theme.applyMode(theme.currentMode() === "light" ? "dark" : "light"),
	});

	cmds.push({
		id: "lang:en",
		title: `${t("Switch language", lng)} → EN`,
		hint: "EN",
		run: () => theme.applyLang("en", { persist: true, reload: true }),
	});
	cmds.push({
		id: "lang:ar",
		title: `${t("Switch language", lng)} → AR`,
		hint: "AR",
		run: () => theme.applyLang("ar", { persist: true, reload: true }),
	});

	cmds.push({
		id: "picker:open",
		title: t("Theme Settings", lng),
		hint: "🎨",
		run: () => themePicker.open(),
	});

	cmds.push({
		id: "workbench:open",
		title: t("Open Workbench", lng),
		hint: "/app/e16-workbench",
		run: () => { window.location.href = "/app/e16-workbench"; },
	});

	cmds.push({
		id: "focus:toggle",
		title: t("Focus Mode", lng),
		hint: "⤢",
		run: () => document.body.classList.toggle("e16-focus-mode"),
	});

	// Recent docs
	try {
		const recent = (window.frappe?.boot?.user?.recent_documents) || [];
		for (const r of recent.slice(0, 8)) {
			if (!r || !r[0] || !r[1]) continue;
			cmds.push({
				id: `recent:${r[0]}:${r[1]}`,
				title: `${r[0]}: ${r[1]}`,
				hint: lng === "ar" ? "حديث" : "Recent",
				run: () => {
					window.location.href = `/app/${encodeURIComponent(r[0].toLowerCase().replace(/ /g, "-"))}/${encodeURIComponent(r[1])}`;
				},
			});
		}
	} catch (e) {}

	return cmds;
}

function open() {
	close();
	const lng = theme.currentLang();
	const root = document.createElement("div");
	root.id = ROOT;
	root.className = "e16-cmdk-backdrop";
	root.setAttribute("role", "dialog");
	root.setAttribute("aria-modal", "true");

	const panel = document.createElement("div");
	panel.className = "e16-cmdk";

	const input = document.createElement("input");
	input.type = "text";
	input.className = "e16-cmdk__input";
	input.placeholder = t("Search commands…", lng);

	const list = document.createElement("ul");
	list.className = "e16-cmdk__list";
	list.setAttribute("role", "listbox");

	panel.appendChild(input);
	panel.appendChild(list);
	root.appendChild(panel);
	document.body.appendChild(root);

	const all = buildCommands();
	let visible = all;
	let active = 0;

	function render(q) {
		visible = fuzzyFilter(q, all, (c) => c.title);
		list.innerHTML = "";
		if (!visible.length) {
			const empty = document.createElement("li");
			empty.className = "e16-cmdk__empty";
			empty.textContent = t("No matches", lng);
			list.appendChild(empty);
			return;
		}
		visible.forEach((c, i) => {
			const li = document.createElement("li");
			li.className = "e16-cmdk__item" + (i === active ? " is-active" : "");
			li.innerHTML = `<span class="e16-cmdk__title"></span><span class="e16-cmdk__hint"></span>`;
			li.querySelector(".e16-cmdk__title").textContent = c.title;
			li.querySelector(".e16-cmdk__hint").textContent = c.hint || "";
			li.addEventListener("click", () => { close(); c.run(); });
			list.appendChild(li);
		});
	}

	function move(delta) {
		if (!visible.length) return;
		active = (active + delta + visible.length) % visible.length;
		render(input.value);
		const el = list.children[active];
		if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
	}

	input.addEventListener("input", () => { active = 0; render(input.value); });
	input.addEventListener("keydown", (ev) => {
		if (ev.key === "ArrowDown") { ev.preventDefault(); move(1); }
		else if (ev.key === "ArrowUp") { ev.preventDefault(); move(-1); }
		else if (ev.key === "Enter") {
			ev.preventDefault();
			const c = visible[active];
			if (c) { close(); c.run(); }
		} else if (ev.key === "Escape") close();
	});

	root.addEventListener("click", (ev) => { if (ev.target === root) close(); });

	render("");
	setTimeout(() => input.focus(), 0);
}

export const commandPalette = {
	mount() {
		const prefs = (window.frappe?.boot?.e16_prefs) || {};
		if (prefs.enable_command_palette === 0) return;
		document.addEventListener("keydown", (ev) => {
			const isMac = navigator.platform.toUpperCase().includes("MAC");
			const trigger = (isMac ? ev.metaKey : ev.ctrlKey) && (ev.key === "k" || ev.key === "K");
			if (trigger) {
				ev.preventDefault();
				const isOpen = !!document.getElementById(ROOT);
				if (isOpen) close(); else open();
			}
		});
	},
	open, close,
};
